import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import OrderNowBanner from "./OrderNowBanner";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const AUTO_ROTATE_MS = 6000;
const SWIPE_THRESHOLD = 50;

const CampaignBannerCarousel = ({ enrollments, onExpireRefresh }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const containerRef = useRef(null);

  const count = enrollments.length;
  const showControls = count > 1;

  // Reset index if enrollments shrink (e.g., one expires and is removed)
  useEffect(() => {
    if (activeIndex >= count) setActiveIndex(0);
  }, [count, activeIndex]);

  // Auto-rotate
  useEffect(() => {
    if (!showControls || isPaused) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % count);
    }, AUTO_ROTATE_MS);
    return () => clearInterval(id);
  }, [count, isPaused, showControls]);

  const goTo = useCallback(
    (index) => setActiveIndex(((index % count) + count) % count),
    [count],
  );
  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  // Pause on hover / focus
  const pause = () => setIsPaused(true);
  const resume = () => setIsPaused(false);

  // Swipe handling (mobile)
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) >= SWIPE_THRESHOLD) {
      diff > 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  if (count === 0) return null;

  // Single enrollment — no carousel, render directly
  if (count === 1) {
    return (
      <div className="mb-4 -mt-4">
        <OrderNowBanner
          campaign={enrollments[0].campaign}
          restaurantDetails={enrollments[0]}
          onExpireRefresh={onExpireRefresh}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative mb-4 -mt-4"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="group"
      aria-label="Active campaign offers"
    >
      {/* Slides */}
      <div className="overflow-hidden rounded-[16px]">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {enrollments.map((enrollment, index) => (
            <div
              key={enrollment.campaign?.code || index}
              className="w-full flex-shrink-0"
              aria-roledescription="slide"
              aria-label={`Campaign ${index + 1} of ${count}`}
              aria-hidden={index !== activeIndex}
            >
              <OrderNowBanner
                campaign={enrollment.campaign}
                restaurantDetails={enrollment}
                onExpireRefresh={onExpireRefresh}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Arrows — desktop only, show on hover */}
      {showControls && (
        <>
          <button
            onClick={goPrev}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 shadow-md transition-colors"
            aria-label="Previous campaign offer"
          >
            <ChevronLeftIcon sx={{ color: "#374151", fontSize: 20 }} />
          </button>
          <button
            onClick={goNext}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 shadow-md transition-colors"
            aria-label="Next campaign offer"
          >
            <ChevronRightIcon sx={{ color: "#374151", fontSize: 20 }} />
          </button>
        </>
      )}

      {/* Dots */}
      {showControls && (
        <div className="flex justify-center gap-1.5 mt-2">
          {enrollments.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`w-2 h-2 rounded-full transition-opacity ${
                index === activeIndex ? "bg-emerald-600 opacity-100" : "bg-gray-400 opacity-40"
              }`}
              aria-label={`Go to campaign ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

CampaignBannerCarousel.propTypes = {
  enrollments: PropTypes.arrayOf(
    PropTypes.shape({
      campaign: PropTypes.object,
      isCampaign: PropTypes.bool,
      scope: PropTypes.string,
      restaurantName: PropTypes.string,
    }),
  ).isRequired,
  onExpireRefresh: PropTypes.func,
};

export default CampaignBannerCarousel;
